import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { mapTripDetail, type RawTripDetail } from '@/lib/api/adapters/trips';
import { queryKeys } from '@/lib/query/query-keys';
import type { TripDetail } from './types';

interface ApiErrorPayload {
  error?: {
    message?: string;
  };
}

function extractErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const parsed = payload as ApiErrorPayload;
    const message = parsed.error?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  return fallback;
}

export interface ExtendTripParams {
  tripId: string;
  endDate: string; // ISO date string (yyyy-MM-dd)
}

async function performExtendTrip({ tripId, endDate }: ExtendTripParams): Promise<TripDetail> {
  const response = await apiFetch(`/api/v1/business-trip-requests/${tripId}/extend`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ end_date: endDate }),
  });
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message = extractErrorMessage(
      payload,
      `Failed to extend trip ${tripId}: ${response.status} ${response.statusText}`
    );
    throw new Error(message);
  }

  if (!payload || typeof payload !== 'object') {
    throw new Error('Trip response is malformed.');
  }

  return mapTripDetail(payload as RawTripDetail);
}

export interface ShortenTripParams {
  tripId: string;
  endDate: string; // ISO date string (yyyy-MM-dd)
}

async function performShortenTrip({ tripId, endDate }: ShortenTripParams): Promise<TripDetail> {
  const response = await apiFetch(`/api/v1/business-trip-requests/${tripId}/shorten`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ end_date: endDate }),
  });
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message = extractErrorMessage(
      payload,
      `Failed to shorten trip ${tripId}: ${response.status} ${response.statusText}`
    );
    throw new Error(message);
  }

  if (!payload || typeof payload !== 'object') {
    throw new Error('Trip response is malformed.');
  }

  return mapTripDetail(payload as RawTripDetail);
}

export function useExtendTrip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: ExtendTripParams) => performExtendTrip(params),
    onSuccess: (trip) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips });
      queryClient.invalidateQueries({ queryKey: queryKeys.tripApprovals });
      if (trip?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.trip(trip.id) });
      }
    },
  });
}

export function useShortenTrip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: ShortenTripParams) => performShortenTrip(params),
    onSuccess: (trip) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips });
      queryClient.invalidateQueries({ queryKey: queryKeys.tripApprovals });
      if (trip?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.trip(trip.id) });
      }
    },
  });
}
